import { GlobalMessages } from '@shared/localization/messages';
import type { DataTableColumn } from 'mantine-datatable';
import { useCallback, useMemo, useState } from 'react';

import IconCaretDown from '@/assets/Icons/IconCaretDown.tsx';
import Dropdown from '@/components/atoms/Dropdown.tsx';
import { useMessages } from '@/hooks/messages';

export interface IColumnSelectorProps<T> {
  onColumnsChange: (data: DataTableColumn<T>[]) => void;
  columns: DataTableColumn<T>[];
  hiddenColumns?: string[];
  label?: string;
}

export const TableColumnsSelector = <T,>({
  columns,
  onColumnsChange,
  hiddenColumns,
  label,
}: IColumnSelectorProps<T>) => {
  const globalMessages = useMessages(GlobalMessages);
  const displayLabel = label ?? globalMessages.columns();

  // EWM-1041: same controlled/uncontrolled split as TablePhasesSelector. The estimation
  // detail tabs pass `hiddenColumns` and derive it back from the `onColumnsChange` payload,
  // so the sync effect re-dispatched a value already held, on a fresh array identity that
  // React cannot bail out of — one extra default-lane commit per checkbox click.
  // <DataTable> passes no `hiddenColumns`, which is what the local fallback serves.
  const [uncontrolledHideCols, setUncontrolledHideCols] = useState<string[]>([]);
  const hideCols = hiddenColumns ?? uncontrolledHideCols;

  const showHideColumns = useCallback(
    (col: string, visibility: boolean) => {
      const copy = visibility ? hideCols.filter((d) => d !== col) : [...hideCols, col];
      // Controlled: the prop wins, so writing the fallback would only schedule an update
      // whose result is never read.
      if (hiddenColumns === undefined) {
        setUncontrolledHideCols(copy);
      }
      onColumnsChange(columns?.filter((column) => !copy.includes(column.accessor as string)));
    },
    [columns, hiddenColumns, hideCols, onColumnsChange],
  );

  const mappedCols = useMemo(
    () =>
      columns?.map((col) => {
        const accessor = col.accessor as string;
        if (!(col.title as string)?.length) return null; // hide columns without title

        return (
          <li
            key={accessor}
            role='none' // stopPropagation only — the interactive element is the checkbox label inside
            className='flex flex-col'
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className='dark:hover:bg-c4 flex items-center px-4 py-3 hover:bg-(--color-light-grey)'>
              <label className='mb-0 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={!hideCols.includes(accessor)}
                  className='form-checkbox mr-1'
                  defaultValue={accessor}
                  onChange={({ target: { checked } }) => {
                    showHideColumns(accessor, checked);
                  }}
                />
                <span className='ltr:ml-2 rtl:mr-2 dark:text-white'>{col.title}</span>
              </label>
            </div>
          </li>
        );
      }),
    [columns, hideCols, showHideColumns],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DropdownComponent = Dropdown as any;

  return (
    <DropdownComponent
      placement='bottom-start'
      offset={[0, 10]}
      btnClassName='!flex h-12 items-center gap-1 rounded-md border bg-white px-4 py-3 text-base hover:border-inherit dark:border-white dark:text-white dark:bg-transparent'
      button={
        <div className='flex gap-1'>
          <span className='ltr:mr-1 rtl:ml-1'>{displayLabel}</span>
          <IconCaretDown className='h-5 w-5' />
        </div>
      }
    >
      <ul className='dark:!bg-c16 !my-0 !min-w-[140px] rounded py-3 opacity-100'>{mappedCols}</ul>
    </DropdownComponent>
  );
};
